import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, Check, Minus, ShieldCheck, UserPlus } from "lucide-react"
import { type FormEvent, useRef, useState } from "react"
import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { Cajon } from "@/componentes/ui/Cajon"
import { Modal } from "@/componentes/ui/Modal"
import { Pestanas } from "@/componentes/ui/Pestanas"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { EditorMenu } from "@/componentes/usuarios/EditorMenu"
import {
  cuerpoDeLaFicha,
  type DatosFicha,
  FICHA_VACIA,
  FichaDelAlta,
} from "@/componentes/usuarios/FichaDelAlta"
import { FichaUsuario, type RolFila, type UsuarioFila } from "@/componentes/usuarios/FichaUsuario"
import { MatrizPermisos } from "@/componentes/usuarios/MatrizPermisos"
import { LARGO_MINIMO_CLAVE } from "@/lib/contrasenas"

/**
 * Alta y administración de usuarios.
 *
 * **No existe registro público en ninguna parte del sistema**: una cuenta nace
 * siempre por invitación de alguien con `usuario:administrar`, y ésta es la
 * pantalla que lo hace. Hasta ahora sólo se podía por `curl`.
 *
 * Dos reglas del servidor se reflejan aquí, y ninguna se reimplementa:
 *
 * - Un usuario `CLIENTE` **exige** `cliente_id`, y un `INTERNO` **no puede**
 *   tenerlo. El formulario muestra u oculta el selector, pero quien decide es el
 *   validador del esquema; si esto se equivocara, el servidor devuelve 422 y los
 *   campos señalados se pintan uno a uno.
 * - Desactivar corta la sesión **al siguiente request**, no al expirar el token:
 *   la dependencia de sesión comprueba `activo` en cada uno.
 *
 * **Tres pestañas, y las tres son cosas distintas.** *Usuarios* son las cuentas;
 * *Roles y permisos* es quién puede qué —lo decide el servidor en cada petición—; y
 * *Menú* es sólo qué se ofrece y en qué orden. Confundir las dos últimas es lo que
 * hizo el 2.0: allí conceder un módulo concedía todas sus operaciones, y esconderlo
 * no cerraba nada porque la autorización era opcional por vista.
 *
 * **El alta es un modal y la edición una ficha lateral**, como en tareas. Son dos
 * gestos distintos: crear una cuenta exige atención completa —correo, contraseña,
 * tipo y roles— y no se hace a medias; revisar a alguien es compararlo con el resto
 * de la tabla, que sigue visible al lado.
 */

type TipoUsuario = "INTERNO" | "CLIENTE"

interface RolCompleto extends RolFila {
  es_sistema: boolean
  permisos: string[]
}

interface ClienteFila {
  id: number
  codigo: string
  razon_social: string
}

/** Los campos que el servidor señaló, tal como llegan en `details.campos`. */
interface CampoInvalido {
  campo: string
  mensaje: string
  tipo: string
}

function camposDelError(error: unknown): CampoInvalido[] {
  if (!(error instanceof ErrorApi) || error.codigo !== "VALIDACION") return []
  const campos = error.detalles?.campos
  return Array.isArray(campos) ? (campos as CampoInvalido[]) : []
}

const ENTRADA = "h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"

type Vista = "usuarios" | "roles" | "menu"

const VISTAS: { valor: Vista; etiqueta: string }[] = [
  { valor: "usuarios", etiqueta: "Usuarios" },
  { valor: "roles", etiqueta: "Roles y permisos" },
  { valor: "menu", etiqueta: "Menú" },
]

export function Usuarios() {
  const [vista, setVista] = useState<Vista>("usuarios")
  const [abierto, setAbierto] = useState(false)
  // La ficha NO se limpia al cerrarse: si se limpiara, se quedaría vacía durante la
  // animación de salida y se vería desaparecer el contenido antes que el panel.
  const [enFicha, setEnFicha] = useState<number | null>(null)
  const [fichaVisible, setFichaVisible] = useState(false)
  const [email, setEmail] = useState("")
  const [nombre, setNombre] = useState("")
  const [password, setPassword] = useState("")
  const [tipo, setTipo] = useState<TipoUsuario>("INTERNO")
  const [clienteId, setClienteId] = useState("")
  const [roles, setRoles] = useState<string[]>([])
  const [ficha, setFicha] = useState<DatosFicha>(FICHA_VACIA)
  const clienteQuery = useQueryClient()

  const estiloEntrada = {
    borderColor: "var(--border)",
    backgroundColor: "var(--background)",
    color: "var(--fg)",
  }

  const usuarios = useQuery({
    queryKey: ["usuarios"],
    queryFn: () => api.get<UsuarioFila[]>("/api/v1/usuarios?limit=100"),
  })
  const catalogoRoles = useQuery({
    queryKey: ["roles"],
    queryFn: () => api.get<RolCompleto[]>("/api/v1/roles"),
  })
  const clientes = useQuery({
    queryKey: ["clientes"],
    queryFn: () => api.get<ClienteFila[]>("/api/v1/clientes?limit=100"),
  })

  function limpiar() {
    setEmail("")
    setNombre("")
    setPassword("")
    setTipo("INTERNO")
    setClienteId("")
    setRoles([])
    setFicha(FICHA_VACIA)
  }

  const crear = useMutation({
    mutationFn: () =>
      api.post("/api/v1/usuarios", {
        email,
        nombre,
        password,
        tipo,
        // `null` y no cadena vacía: el esquema espera un entero o nada.
        cliente_id: tipo === "CLIENTE" && clienteId ? Number(clienteId) : null,
        roles,
        // ⚠ El expediente viaja EN EL MISMO alta: el servidor crea la cuenta y la
        // ficha en una sola transacción. Mandarlo aparte dejaría una cuenta sin
        // expediente cada vez que la ficha fallara.
        ficha: cuerpoDeLaFicha(ficha),
      }),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["usuarios"] })
      // La lista de expedientes sin cuenta acaba de cambiar si se enlazó uno.
      clienteQuery.invalidateQueries({ queryKey: ["personas-sin-cuenta"] })
      setAbierto(false)
      limpiar()
    },
  })

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    crear.mutate()
  }

  const invalidos = camposDelError(crear.error)
  // Se busca en la lista viva y no se copia al abrir: así, al guardar, la ficha
  // enseña lo que quedó guardado en vez de lo que había cuando se pulsó la fila.
  const seleccionado = usuarios.data?.find((u) => u.id === enFicha)
  const tablaUsuarios = useRef<HTMLDivElement | null>(null)

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Usuarios</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            {vista === "usuarios" && usuarios.data ? `${usuarios.data.length} cuentas` : " "}
          </p>
        </div>
        {vista === "usuarios" && (
          <div className="flex flex-wrap items-start gap-2">
            <Exportar
              titulo="Usuarios"
              subtitulo="Cuentas del sistema, sus roles y su enlace con Telegram"
              contenido={tablaUsuarios}
            />
            <button
              type="button"
              onClick={() => setAbierto(true)}
              aria-haspopup="dialog"
              className="boton-primario flex h-9 items-center gap-2 px-3 text-sm font-medium"
            >
              <UserPlus size={16} aria-hidden="true" />
              Invitar usuario
            </button>
          </div>
        )}
      </div>

      {/* Las pestañas ANTES de todo lo demás: primero qué se administra, después
          cómo. Al revés se lee como si el contenido cambiara el modo. */}
      <Pestanas
        pestanas={VISTAS}
        activa={vista}
        onCambiar={setVista}
        etiqueta="Qué se administra"
        idPanel="panel-admin"
        prefijoId="pestana-admin"
      />

      <div id="panel-admin" role="tabpanel" aria-labelledby={`pestana-admin-${vista}`}>
        {vista === "roles" && <MatrizPermisos />}
        {vista === "menu" && <EditorMenu />}
      </div>

      <Modal
        abierto={abierto}
        titulo="Invitar usuario"
        descripcion="No hay registro público: una cuenta nace siempre por invitación."
        onCerrar={() => setAbierto(false)}
      >
        <form onSubmit={enviar} aria-label="Invitar usuario">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="u-email" className="mb-1 block text-xs font-medium uppercase">
                Correo
              </label>
              <input
                id="u-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={ENTRADA}
                style={estiloEntrada}
              />
            </div>

            <div>
              <label htmlFor="u-nombre" className="mb-1 block text-xs font-medium uppercase">
                Nombre
              </label>
              <input
                id="u-nombre"
                required
                minLength={2}
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                className={ENTRADA}
                style={estiloEntrada}
              />
            </div>

            <div>
              <label htmlFor="u-clave" className="mb-1 block text-xs font-medium uppercase">
                Contraseña inicial (mínimo {LARGO_MINIMO_CLAVE} caracteres)
              </label>
              {/* Sin bloqueo de pegado: romper los gestores de contraseñas empeora
                  la seguridad real, y WCAG 3.3.8 lo prohíbe explícitamente. */}
              <input
                id="u-clave"
                type="password"
                required
                minLength={LARGO_MINIMO_CLAVE}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={ENTRADA}
                style={estiloEntrada}
              />
            </div>

            <div>
              <label htmlFor="u-tipo" className="mb-1 block text-xs font-medium uppercase">
                Tipo
              </label>
              <select
                id="u-tipo"
                value={tipo}
                onChange={(e) => {
                  setTipo(e.target.value as TipoUsuario)
                  setClienteId("")
                  // El paquete de roles razonable cambia por completo entre los dos.
                  setRoles([])
                }}
                className={ENTRADA}
                style={estiloEntrada}
              >
                <option value="INTERNO">Interno (personal de SETIECA)</option>
                <option value="CLIENTE">Cliente (portal del importador)</option>
              </select>
            </div>

            {tipo === "CLIENTE" && (
              <div className="md:col-span-2">
                <label htmlFor="u-cliente" className="mb-1 block text-xs font-medium uppercase">
                  Importador al que pertenece
                </label>
                <select
                  id="u-cliente"
                  required
                  value={clienteId}
                  onChange={(e) => setClienteId(e.target.value)}
                  className={ENTRADA}
                  style={estiloEntrada}
                >
                  <option value="">Selecciona un importador</option>
                  {clientes.data?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.codigo} · {c.razon_social}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
                  Obligatorio. Es lo que ancla al usuario a sus despachos: sin él no habría por
                  dónde recortar su alcance y vería los de todos los importadores.
                </p>
              </div>
            )}

            <fieldset className="md:col-span-2">
              <legend className="mb-1 text-xs font-medium uppercase">Roles</legend>
              <div className="flex flex-wrap gap-3">
                {catalogoRoles.data?.map((r) => (
                  <label key={r.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={roles.includes(r.nombre)}
                      onChange={(e) =>
                        setRoles((prev) =>
                          e.target.checked
                            ? [...prev, r.nombre]
                            : prev.filter((n) => n !== r.nombre),
                        )
                      }
                    />
                    {r.nombre}
                    <span className="tabular text-xs" style={{ color: "var(--fg-muted)" }}>
                      ({r.permisos.length})
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>

          {crear.isError && (
            <div
              role="alert"
              className="mt-4 rounded-[var(--radius-control)] border p-3 text-sm"
              style={{ borderColor: "var(--destructive)", color: "var(--destructive)" }}
            >
              <p className="flex items-center gap-2 font-medium">
                <AlertCircle size={16} aria-hidden="true" />
                {crear.error instanceof ErrorApi
                  ? crear.error.message
                  : "No se pudo crear el usuario"}
              </p>
              {/* Los campos que el servidor señaló, uno a uno. Un «datos de entrada
                  invalidos» a secas obliga a adivinar cuál de ocho campos falló. */}
              {invalidos.length > 0 && (
                <ul className="mt-2 ml-5 list-disc">
                  {invalidos.map((c) => (
                    <li key={c.campo}>
                      <strong>{c.campo}</strong>: {c.mensaje}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <FichaDelAlta ficha={ficha} onCambiar={setFicha} nombreDelUsuario={nombre} />

          <button
            type="submit"
            disabled={crear.isPending}
            className="mt-4 h-9 rounded-[var(--radius-control)] px-3 text-sm font-medium disabled:opacity-50"
            style={{ backgroundColor: "var(--primary)", color: "var(--primary-fg)" }}
          >
            {crear.isPending ? "Creando…" : "Crear usuario"}
          </button>
        </form>
      </Modal>

      {vista === "usuarios" && usuarios.isPending && (
        <p className="py-12 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando usuarios…
        </p>
      )}

      {vista === "usuarios" && usuarios.isError && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          No se pudieron cargar los usuarios
        </p>
      )}

      {vista === "usuarios" && usuarios.data && (
        <div ref={tablaUsuarios}>
          <Tabla etiqueta="Usuarios">
            <Cabecera>
              <Th>Correo</Th>
              <Th>Nombre</Th>
              <Th>Tipo</Th>
              <Th>Roles</Th>
              <Th>Telegram</Th>
              <Th>Estado</Th>
            </Cabecera>
            <Cuerpo>
              {usuarios.data.map((u) => (
                <Fila
                  key={u.id}
                  data-testid={`usuario-${u.id}`}
                  aria-current={fichaVisible && enFicha === u.id ? true : undefined}
                  className={
                    fichaVisible && enFicha === u.id ? "bg-[var(--surface-elevated)]" : undefined
                  }
                >
                  <Td className="font-medium" data-exportar-valor={u.email}>
                    {/* El correo ES el control de apertura: un `<button>` y no una fila
                      con `onClick`, porque una fila no recibe foco ni responde a
                      Enter, y un lector no la anuncia como pulsable. */}
                    <button
                      type="button"
                      data-testid={`abrir-usuario-${u.id}`}
                      onClick={() => {
                        setEnFicha(u.id)
                        setFichaVisible(true)
                      }}
                      className="text-left underline-offset-2 hover:underline"
                    >
                      {u.email}
                    </button>
                  </Td>
                  <Td>{u.nombre}</Td>
                  <Td style={{ color: "var(--fg-muted)" }}>{u.tipo}</Td>
                  <Td data-exportar-valor={u.roles.join(", ") || "sin rol"}>
                    <span className="flex flex-wrap items-center gap-1">
                      {u.roles.length === 0 ? (
                        <span style={{ color: "var(--fg-muted)" }}>sin rol</span>
                      ) : (
                        u.roles.map((r) => (
                          <span
                            key={r}
                            className="inline-flex items-center gap-1 rounded-[var(--radius-control)] border px-1.5 py-0.5 text-xs"
                            style={{ borderColor: "var(--border)" }}
                          >
                            <ShieldCheck size={12} aria-hidden="true" />
                            {r}
                          </span>
                        ))
                      )}
                    </span>
                  </Td>
                  <Td
                    data-testid={`telegram-${u.id}`}
                    data-exportar-valor={u.telegram_vinculado ? "Enlazado" : "Sin enlazar"}
                    style={{
                      color: u.telegram_vinculado ? "var(--success)" : "var(--fg-muted)",
                    }}
                  >
                    {/* Icono Y texto para el lector, no sólo color: aquí el estado es
                      el contenido de la celda (WCAG 1.4.1). */}
                    <span className="flex items-center gap-1">
                      {u.telegram_vinculado ? (
                        <Check size={14} aria-hidden={true} />
                      ) : (
                        <Minus size={14} aria-hidden={true} />
                      )}
                      <span className="sr-only">
                        {u.telegram_vinculado ? "Enlazado" : "Sin enlazar"}
                      </span>
                    </span>
                  </Td>
                  <Td style={{ color: u.activo ? "var(--success)" : "var(--fg-muted)" }}>
                    {u.activo ? "activo" : "desactivado"}
                  </Td>
                </Fila>
              ))}
            </Cuerpo>
          </Tabla>
        </div>
      )}

      {/* La ficha NO es un modal: revisar a alguien es compararlo con el resto de
          la tabla, y una capa oscura tapa justo eso. */}
      <Cajon abierto={fichaVisible} titulo="Usuario" onCerrar={() => setFichaVisible(false)}>
        {seleccionado !== undefined && (
          <FichaUsuario
            key={seleccionado.id}
            usuario={seleccionado}
            roles={catalogoRoles.data ?? []}
            onGuardado={() => clienteQuery.invalidateQueries({ queryKey: ["usuarios"] })}
          />
        )}
      </Cajon>
    </div>
  )
}
